// ScoresHeatmap.js — the home page: a toolbar (h1 + axes toggle + season
// picker), the grid, and a side panel with stats, the colour legend, and
// highlights (below the grid on narrow screens). Data, grid, legend, hover
// card, and drawer live in components/heatmap/*.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ErrorLandingPage from "./ErrorLandingPage";
import SEO from "./components/SEO";
import { HEADER_H } from "./components/AppHeader";
import SeasonSelect from "./components/heatmap/SeasonSelect";
import AxesToggle from "./components/heatmap/AxesToggle";
import HeatmapGrid, { CELL_GAP } from "./components/heatmap/HeatmapGrid";
import HeatmapLegend from "./components/heatmap/HeatmapLegend";
import HeatmapHoverCard from "./components/heatmap/HeatmapHoverCard";
import HeatmapSkeleton from "./components/heatmap/HeatmapSkeleton";
import HeatmapStats from "./components/heatmap/HeatmapStats";
import InsightsList from "./components/heatmap/InsightsList";
import AdSlot from "./components/AdSlot";
import ScoreDetailsModal from "./components/heatmap/ScoreDetailsModal";
import useHeatmapData from "./components/heatmap/useHeatmapData";
import {
  ALL_LABEL,
  HOME_AWAY,
  isCurrentSeason,
  legendBands,
  shortSeasonLabel,
} from "./components/heatmap/utils";
import { seasonPath } from "./components/clubs/seasons";
import HomeAbout from "./components/heatmap/HomeAbout";
import { BRAND } from "./theme";

const PANEL_W = 300;
// Vertical space around the grid on desktop: toolbar, x-axis numbers and
// title, bottom padding. Keeps the grid inside one viewport. TOOLBAR_H is the
// one-line toolbar; a taller one (the heading wraps on small laptops) is
// measured instead.
const TOOLBAR_H = 88;
const X_AXIS_H = 34 + 36;
const BOTTOM_PAD = 28;

// Largest cell size that fits the viewport: by width below md, by width and
// height (one screen, above the footer) on desktop.
function computeCellSize(n, isNarrow, toolbarH = TOOLBAR_H) {
  const W = window.innerWidth;
  const H = window.innerHeight;
  if (isNarrow) {
    // page padding + y title + y numbers + gaps
    const chromeW = 24 + 18 + 24 + CELL_GAP * n;
    return Math.max(24, Math.min(64, Math.floor((W - chromeW) / n)));
  }
  // page padding + panel + gap + y title + y numbers + gaps
  const chromeW = 48 + PANEL_W + 32 + 30 + 40 + CELL_GAP * n;
  const chromeH = HEADER_H.desktop + Math.max(TOOLBAR_H, toolbarH) + X_AXIS_H + BOTTOM_PAD + CELL_GAP * n;
  const size = Math.min(Math.floor((W - chromeW) / n), Math.floor((H - chromeH) / n));
  return Math.max(34, Math.min(96, size));
}

const PANEL_CARD_SX = {
  p: 2,
  borderRadius: 3,
  background: "rgba(20,20,26,0.72)",
  border: "1px solid rgba(255,255,255,0.08)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
};

function getFirstAndLatestFromRaw(list) {
  if (!list || !list.length) return { first: null, latest: null };
  const latest = list[0];
  const first = list[list.length - 1];
  const pick = (m) => ({
    utcDate: m.utcDate,
    highScore: m.highScore,
    lowScore: m.lowScore,
    highTeam: m.highTeam,
    lowTeam: m.lowTeam,
    highIsHome: m.highIsHome,
  });
  return { first: pick(first), latest: pick(latest) };
}

function getOccurrences(key, { isAllSeasons, scoreSummaryMap, scoreMap }) {
  let firstOcc = null;
  let latestOcc = null;
  if (isAllSeasons) {
    const cell = scoreSummaryMap[key];
    if (cell) {
      firstOcc = cell.firstOccurrence || null;
      latestOcc = cell.latestOccurrence || null;
    }
  } else {
    const { first, latest } = getFirstAndLatestFromRaw(scoreMap[key]);
    firstOcc = first;
    latestOcc = latest;
  }
  const showLatest =
    firstOcc &&
    latestOcc &&
    firstOcc.utcDate &&
    latestOcc.utcDate &&
    firstOcc.utcDate !== latestOcc.utcDate;
  return { firstOcc, latestOcc, showLatest };
}

export default function ScoresHeatmap() {
  const theme = useTheme();
  // Below md the side panel moves under the grid and the grid is sized by width.
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  const [season, setSeason] = useState(ALL_LABEL);
  const [axes, setAxes] = useState(HOME_AWAY);
  const [selectedScore, setSelectedScore] = useState(null);
  const [hoverData, setHoverData] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });
  const data = useHeatmapData(season, axes);
  const {
    isAllSeasons,
    loading,
    error,
    scoreMap,
    scoreSummaryMap,
    xAxis,
    yAxis,
    maxCount,
    retry,
  } = data;
  const bands = useMemo(() => legendBands(maxCount), [maxCount]);
  // Sized on the first render (not after an effect) so a pre-rendered page
  // doesn't jump when the app takes over.
  const [cellSize, setCellSize] = useState(() => (xAxis.length ? computeCellSize(xAxis.length, isNarrow) : 56));
  const toolbarRef = useRef(null);

  useEffect(() => {
    setSelectedScore(null);
    setHoverData(null);
  }, [season, axes]);

  useEffect(() => {
    const updateCellSize = () => {
      if (xAxis.length) setCellSize(computeCellSize(xAxis.length, isNarrow, toolbarRef.current?.offsetHeight));
    };
    updateCellSize();
    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, [xAxis, isNarrow]);

  const handleCellClick = (key) => {
    if (isAllSeasons) {
      if (!scoreSummaryMap[key]) return;
    } else if (!scoreMap[key]?.length) return;
    setSelectedScore(key);
  };

  const handleCellHover = (e, key, count) => {
    setHoverPosition({ top: e.clientY + 14, left: e.clientX + 14 });
    const { firstOcc, latestOcc, showLatest } = getOccurrences(key, {
      isAllSeasons,
      scoreSummaryMap,
      scoreMap,
    });
    setHoverData({
      key,
      count,
      firstOccurrence: firstOcc,
      latestOccurrence: showLatest ? latestOcc : null,
    });
  };

  const heading = isAllSeasons
    ? "Every Premier League scoreline since the league began in 1992"
    : `Every Premier League scoreline of the ${shortSeasonLabel(season)} season${isCurrentSeason(season) ? " so far" : ""}`;
  const toolbar = (
    <Box
      ref={toolbarRef}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: 1.5,
        px: { xs: 1.5, md: 3 },
        pt: { xs: 1.75, md: 2.25 },
        pb: { xs: 1.5, md: 1.75 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            color: "#fff",
            fontSize: { xs: 17, md: 21 },
            fontWeight: 800,
            letterSpacing: "-0.015em",
            lineHeight: 1.2,
          }}
        >
          {heading}
        </Typography>
        <Typography sx={{ color: BRAND.textSecondary, fontSize: { xs: 12.5, md: 13.5 }, mt: 0.4 }}>
          Each square shows how often that final score has happened. Tap one for its full history.
          {!isAllSeasons && (
            <>
              {" "}
              <Box
                component={RouterLink}
                to={seasonPath(Number(season.slice(0, 4)))}
                sx={{ color: BRAND.cyan, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}
              >
                {shortSeasonLabel(season)} table and records →
              </Box>
            </>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", flexShrink: 0 }}>
        <AxesToggle axes={axes} onChange={setAxes} />
        <SeasonSelect season={season} onChange={setSeason} />
      </Box>
    </Box>
  );

  const seo = (
    <SEO
      title=""
      description="Every Premier League scoreline since 1992 on one interactive grid: how often each final score has happened, the rarest results, and the scores never seen."
      path="/"
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        {toolbar}
        <HeatmapSkeleton isMobile={isNarrow} />
      </>
    );
  }

  if (error) {
    return (
      <>
        {seo}
        <ErrorLandingPage onRetry={retry} />
      </>
    );
  }

  const { firstOcc, latestOcc, showLatest } = selectedScore
    ? getOccurrences(selectedScore, { isAllSeasons, scoreSummaryMap, scoreMap })
    : { firstOcc: null, latestOcc: null, showLatest: false };

  const stats = (
    <Box sx={PANEL_CARD_SX}>
      <HeatmapStats scoreSummaryMap={scoreSummaryMap} scoreMap={scoreMap} season={season} axes={axes} />
    </Box>
  );
  const legend = (
    <Box sx={PANEL_CARD_SX}>
      <HeatmapLegend bands={bands} horizontal={isNarrow} />
    </Box>
  );
  const insights = (
    <Box sx={PANEL_CARD_SX}>
      <InsightsList
        scoreSummaryMap={scoreSummaryMap}
        scoreMap={scoreMap}
        season={season}
        axes={axes}
        onClick={setSelectedScore}
      />
    </Box>
  );

  return (
    <>
      {seo}
      {toolbar}
      <Box
        sx={{
          display: "flex",
          flexDirection: isNarrow ? "column" : "row",
          alignItems: isNarrow ? "stretch" : "flex-start",
          justifyContent: "center",
          gap: isNarrow ? 2 : 4,
          px: isNarrow ? 1.5 : 3,
          pb: `${BOTTOM_PAD}px`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", overflowX: "auto" }}>
          <HeatmapGrid
            xAxis={xAxis}
            yAxis={yAxis}
            cellSize={cellSize}
            isMobile={isNarrow}
            isAllSeasons={isAllSeasons}
            axes={axes}
            scoreMap={scoreMap}
            scoreSummaryMap={scoreSummaryMap}
            bands={bands}
            highlightKey={hoverData?.key}
            onCellClick={handleCellClick}
            onCellHover={handleCellHover}
            onCellLeave={() => setHoverData(null)}
          />
        </Box>
        <Box
          sx={{
            width: isNarrow ? "100%" : PANEL_W,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {isNarrow ? (
            <>
              {legend}
              {stats}
              {insights}
            </>
          ) : (
            <>
              {stats}
              {legend}
              {insights}
            </>
          )}
          <AdSlot position="home" />
        </Box>
      </Box>

      <HomeAbout />

      {!isNarrow && (
        <HeatmapHoverCard hoverData={hoverData} position={hoverPosition} axes={axes} />
      )}

      <ScoreDetailsModal
        selectedScore={selectedScore}
        onClose={() => setSelectedScore(null)}
        isAllSeasons={isAllSeasons}
        season={season}
        axes={axes}
        scoreSummaryMap={scoreSummaryMap}
        scoreMap={scoreMap}
        firstOccurrence={firstOcc}
        latestOccurrence={latestOcc}
        showLatest={showLatest}
      />
    </>
  );
}
